import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Alert,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

import Seo from "../components/Seo";
import API from "../api/api";
import "./DownloadsPage.css";

function DownloadsPage() {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [downloadError, setDownloadError] = useState("");

    useEffect(() => {
        const fetchFiles = async () => {
            try {
                setLoading(true);
                const response = await API.get("/downloads/public/");
                setFiles(response.data || []);
            } catch (err) {
                console.error(err);
                setError("Не удалось загрузить список файлов");
            } finally {
                setLoading(false);
            }
        };

        const checkAuthStatus = async () => {
            try {
                const response = await API.get("/users/me/", {
                    skipAuthRefresh: true,
                });
                if (response.status === 200) {
                    setIsAuthenticated(true);
                }
            } catch (err) {
                setIsAuthenticated(false);
            }
        };

        fetchFiles();
        checkAuthStatus();
    }, []);

    const getFilenameFromDisposition = (disposition) => {
        if (!disposition) return null;

        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match?.[1]) {
            return decodeURIComponent(utf8Match[1]);
        }

        const asciiMatch = disposition.match(/filename="?([^"]+)"?/i);
        if (asciiMatch?.[1]) {
            return asciiMatch[1];
        }

        return null;
    };

    const handleDownload = async (id, fallbackName) => {
        if (!isAuthenticated) {
            setDownloadError("Для скачивания файла необходимо авторизоваться.");
            return;
        }

        try {
            const response = await API.get(`/downloads/public/${id}/download/`, {
                responseType: "blob",
            });

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            const disposition = response.headers["content-disposition"];
            const serverFilename = getFilenameFromDisposition(disposition);
            const filename = serverFilename || fallbackName || `file-${id}`;

            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            setError("Ошибка при скачивании файла");
        }
    };

    const reports = files.filter((item) => item.category === "report");
    const templates = files.filter((item) => item.category === "template");

    const getIcon = (category) => {
        if (category === "report") {
            return <DescriptionOutlinedIcon fontSize="small" />;
        }
        return <TableChartOutlinedIcon fontSize="small" />;
    };

    return (
        <>
            <Seo
                title="Загрузки – Qarzhy"
                description="Скачивание внешних отчетов 1С и шаблонов Excel для подготовки данных к сверке."
                keywords="скачать внешний отчет 1С, шаблон excel, сверка платежных поручений"
                url="https://qarzhy.kz/downloads"
            />

            <Container maxWidth="lg" className="downloads-page">
                <section className="downloads-hero-card">
                    <div className="downloads-hero-badge">
                        Материалы для выгрузки и подготовки данных
                    </div>

                    <div className="downloads-hero-head">
                        <div>
                            <h1 className="downloads-page__title">Загрузки</h1>
                            <p className="downloads-page__subtitle">
                                Здесь размещены внешние отчеты 1С и шаблоны Excel для подготовки
                                данных к сверке банковских платежных поручений, кадровых данных
                                и конвертации платежек в список сотрудников.
                            </p>
                        </div>

                        <div className="downloads-hero-stat">
                            <Inventory2OutlinedIcon fontSize="small" />
                            <span>{files.length} файлов</span>
                        </div>
                    </div>
                </section>

                {error ? (
                    <Alert
                        severity="error"
                        sx={{ mb: 2, borderRadius: "16px" }}
                        onClose={() => setError("")}
                    >
                        {error}
                    </Alert>
                ) : null}

                {downloadError ? (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2, borderRadius: "16px" }}
                        onClose={() => setDownloadError("")}
                    >
                        {downloadError}
                    </Alert>
                ) : null}

                <section className="downloads-card">
                    <div className="downloads-card-kicker">Основные файлы</div>
                    <h2 className="downloads-card-title">Внешние отчеты и шаблоны</h2>

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <div className="downloads-table-wrap">
                            <table className="downloads-table">
                                <thead>
                                    <tr>
                                        <th>№</th>
                                        <th>Имя файла</th>
                                        <th>Тип</th>
                                        <th>Система / конфигурация</th>
                                        <th>Версия</th>
                                        <th>Формат</th>
                                        <th>Действие</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {files.map((item, index) => (
                                        <tr key={item.id}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <div className="downloads-file-cell">
                                                    <div className="downloads-file-icon">
                                                        {getIcon(item.category)}
                                                    </div>

                                                    <div>
                                                        <div className="downloads-file-title">
                                                            {item.title}
                                                        </div>

                                                        {item.description ? (
                                                            <div className="downloads-file-description">
                                                                {item.description}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <Chip
                                                    label={item.category_display}
                                                    size="small"
                                                    className="downloads-chip"
                                                />
                                            </td>
                                            <td>{item.system_name || "—"}</td>
                                            <td>{item.version || "—"}</td>
                                            <td>{item.file_ext || "—"}</td>
                                            <td>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<DownloadRoundedIcon />}
                                                    onClick={() => handleDownload(item.id, item.file_name || item.title)}
                                                    sx={{
                                                        borderRadius: 2.5,
                                                        textTransform: "none",
                                                        fontWeight: 700,
                                                        boxShadow: "none",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    Скачать
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                <div className="downloads-grid">
                    <section className="downloads-card">
                        <div className="downloads-card-kicker">1С</div>
                        <h3 className="downloads-card-title downloads-card-title--small">
                            Внешние отчеты
                        </h3>

                        <div className="downloads-list">
                            {reports.length ? (
                                reports.map((item) => (
                                    <div className="downloads-list-item" key={item.id}>
                                        <div>
                                            <div className="downloads-list-title">{item.title}</div>
                                            <div className="downloads-list-meta">
                                                {item.system_name || "Без указания системы"}
                                                {item.version ? ` • ${item.version}` : ""}
                                            </div>
                                        </div>

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<DownloadRoundedIcon />}
                                            onClick={() => handleDownload(item.id, item.file_name || item.title)}
                                            sx={{
                                                borderRadius: 2.5,
                                                textTransform: "none",
                                                fontWeight: 700,
                                                flexShrink: 0,
                                            }}
                                        >
                                            Скачать
                                        </Button>
                                    </div>
                                ))
                            ) : (
                                <div className="downloads-empty">Файлы пока не добавлены</div>
                            )}
                        </div>
                    </section>

                    <section className="downloads-card">
                        <div className="downloads-card-kicker">Excel</div>
                        <h3 className="downloads-card-title downloads-card-title--small">
                            Шаблоны для загрузки
                        </h3>

                        <div className="downloads-list">
                            {templates.length ? (
                                templates.map((item) => (
                                    <div className="downloads-list-item" key={item.id}>
                                        <div>
                                            <div className="downloads-list-title">{item.title}</div>
                                            <div className="downloads-list-meta">
                                                {item.description || "Шаблон для самостоятельной выгрузки"}
                                            </div>
                                        </div>

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<DownloadRoundedIcon />}
                                            onClick={() => handleDownload(item.id, item.file_name || item.title)}
                                            sx={{
                                                borderRadius: 2.5,
                                                textTransform: "none",
                                                fontWeight: 700,
                                                flexShrink: 0,
                                            }}
                                        >
                                            Скачать
                                        </Button>
                                    </div>
                                ))
                            ) : (
                                <div className="downloads-empty">Файлы пока не добавлены</div>
                            )}
                        </div>
                    </section>
                </div>
            </Container>
        </>
    );
}

export default DownloadsPage;